// frontend/src/components/common/MultiSelectDropdown.jsx
// Dropdown multi-selección con checkboxes: al abrir muestra la lista completa
// (sin texto precargado), permite tipear para filtrar, y mantiene el panel
// abierto entre selecciones para poder elegir varios valores.
import React, { useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import useNavegacionTeclado from '../../hooks/useNavegacionTeclado';

const Wrapper = styled.div`
  position: relative;
`;

const Trigger = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.4rem;
  padding: ${({ $size }) => ($size === 'sm' ? '0.3rem 0.5rem' : '0.4rem 0.6rem')};
  min-height: ${({ $size }) => ($size === 'sm' ? '30px' : '34px')};
  font-size: ${({ $size }) => ($size === 'sm' ? '0.8rem' : '0.875rem')};
  font-family: inherit;
  text-align: left;
  color: ${({ theme }) => theme.colors.txtPrincipal};
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.borderDefault};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  cursor: pointer;
  transition-property: border-color, box-shadow;
  transition-duration: 0.2s;
  transition-timing-function: ease;

  &:focus-visible {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: 0 0 0 3px ${({ theme }) => `${theme.colors.primary}20`};
  }

  &:disabled {
    background: ${({ theme }) => theme.colors.surfaceDisabled};
    color: ${({ theme }) => theme.colors.txtSecondary};
    cursor: not-allowed;
  }
`;

const TriggerLabel = styled.span`
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  color: ${({ $vacio, theme }) => ($vacio ? theme.colors.txtSecondary : theme.colors.txtPrincipal)};
`;

const Chevron = styled.i`
  flex-shrink: 0;
  color: ${({ theme }) => theme.colors.txtSecondary};
`;

const Panel = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  top: calc(100% + 4px);
  z-index: ${({ theme }) => theme.zIndex.fixed || 1030};
  border: 1px solid ${({ theme }) => theme.colors.borderDefault};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  background: ${({ theme }) => theme.colors.surface};
  box-shadow: ${({ theme }) => theme.shadows.sm};
  overflow: hidden;
  min-width: 200px;
`;

const BuscadorWrap = styled.div`
  position: relative;
  border-bottom: 1px solid ${({ theme }) => theme.colors.borderDefault};
`;

const Buscador = styled.input`
  width: 100%;
  padding: 0.35rem 0.6rem;
  border: none;
  background: ${({ theme }) => theme.colors.surface};
  color: ${({ theme }) => theme.colors.txtPrincipal};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};

  &:focus { outline: none; }
`;

const Lista = styled.div`
  max-height: 30vh;
  overflow-y: auto;
`;

const Fila = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 8px;
  cursor: pointer;
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.txtPrincipal};
  font-weight: ${({ $destacado, theme }) => ($destacado ? theme.typography.fontWeight.semibold : theme.typography.fontWeight.regular)};

  background: ${({ $activo, theme }) => ($activo ? theme.colors.opcionActivaFondo : 'transparent')};
  box-shadow: ${({ $activo, theme }) => ($activo ? `inset 3px 0 0 ${theme.colors.opcionActivaBorde}` : 'none')};

  &:hover {
    background: ${({ $activo, theme }) => ($activo ? theme.colors.opcionActivaFondo : theme.colors.surfaceHover)};
  }
`;

const CheckboxBox = styled.span`
  flex-shrink: 0;
  width: 16px;
  height: 16px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid ${({ theme, $checked }) => ($checked ? theme.colors.primary : theme.colors.borderDefault)};
  border-radius: ${({ theme }) => theme.borderRadius.xs || '3px'};
  background: ${({ theme, $checked }) => ($checked ? theme.colors.primary : theme.colors.transparent)};
  color: ${({ theme }) => theme.colors.onPrimary};
  font-size: 0.65rem;
`;

const Separador = styled.div`
  border-bottom: 1px solid ${({ theme }) => theme.colors.borderDefault};
`;

const EmptyState = styled.div`
  padding: 8px 10px;
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.txtSecondary};
`;

export default function MultiSelectDropdown({
  className,
  options = [],
  value = [],
  onChange,
  placeholder = 'Buscar...',
  etiquetaTodos = 'Todas',
  emptyMessage = 'Sin resultados',
  disabled = false,
  size = 'sm',
}) {
  const wrapperRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');

  useEffect(() => {
    if (!open) setQuery('');
  }, [open]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!wrapperRef.current?.contains(event.target)) {
        setOpen(false);
      }
    };
    // Escape lo maneja el onKeyDown del buscador: en document, el Escape de
    // CUALQUIER filtro cerraba todos los desplegables abiertos a la vez.
    document.addEventListener('mousedown', handleClickOutside, true);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside, true);
    };
  }, []);

  const filteredOptions = useMemo(() => {
    const term = query.trim().toLowerCase();
    if (!term) return options;
    return options.filter((opt) => opt.label.toLowerCase().includes(term));
  }, [options, query]);

  const seleccionLabel = useMemo(() => {
    if (!value || value.length === 0) return etiquetaTodos;
    if (value.length === 1) {
      const opt = options.find((o) => String(o.value) === String(value[0]));
      return opt ? opt.label : etiquetaTodos;
    }
    return `${value.length} seleccionadas`;
  }, [value, options, etiquetaTodos]);

  const toggleValor = (valor) => {
    const valorStr = String(valor);
    const yaIncluido = (value || []).some((v) => String(v) === valorStr);
    const nuevo = yaIncluido
      ? value.filter((v) => String(v) !== valorStr)
      : [...(value || []), valorStr];
    onChange?.(nuevo);
  };

  const todosLosValores = useMemo(() => options.map((opt) => String(opt.value)), [options]);
  const todasMarcada = todosLosValores.length > 0 && (value || []).length === todosLosValores.length;

  // Tildar "Todas" marca cada elemento de la lista (para destildar después los que no se quieran ver);
  // destildarla los limpia a todos.
  const toggleTodas = () => onChange?.(todasMarcada ? [] : todosLosValores);

  // La fila "Todas" entra en la navegación como un elemento más: es la primera
  // de la lista visible, así que si no estuviera acá las flechas la saltearían
  // y sería la única fila del panel inalcanzable por teclado.
  const filasNavegables = useMemo(
    () => [{ __todas: true }, ...filteredOptions],
    [filteredOptions]
  );

  const {
    indiceActivo,
    setIndiceActivo,
    onKeyDown,
    registrarOpcion,
    propsAriaInput,
  } = useNavegacionTeclado({
    items: filasNavegables,
    abierto: open,
    setAbierto: setOpen,
    onElegir: (fila) => (fila.__todas ? toggleTodas() : toggleValor(fila.value)),
    onCerrar: () => setOpen(false),
    // Selección múltiple: Enter marca/desmarca y la lista sigue abierta para
    // poder tildar la siguiente sin volver a abrirla.
    cerrarAlElegir: false,
    saltarAlSiguiente: false,
  });

  return (
    <Wrapper ref={wrapperRef} className={className}>
      <Trigger
        type="button"
        $size={size}
        disabled={disabled}
        onClick={() => setOpen((prev) => !prev)}
      >
        <TriggerLabel $vacio={!value || value.length === 0}>{seleccionLabel}</TriggerLabel>
        <Chevron className={`bi bi-chevron-${open ? 'up' : 'down'}`} aria-hidden="true" />
      </Trigger>

      {open && !disabled ? (
        <Panel>
          <BuscadorWrap>
            <Buscador
              {...propsAriaInput}
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={onKeyDown}
              placeholder={placeholder}
              autoComplete="off"
            />
          </BuscadorWrap>
          <Lista role="listbox" aria-multiselectable="true">
            <Fila
              $destacado
              id="opcion-0"
              ref={registrarOpcion(0)}
              role="option"
              aria-selected={todasMarcada}
              $activo={indiceActivo === 0}
              onMouseEnter={() => setIndiceActivo(0)}
              onMouseDown={(e) => e.preventDefault()}
              onClick={toggleTodas}
            >
              <CheckboxBox $checked={todasMarcada}>
                {todasMarcada ? <i className="bi bi-check-lg" /> : null}
              </CheckboxBox>
              <span>{etiquetaTodos}</span>
            </Fila>
            <Separador />
            {filteredOptions.length === 0 ? (
              <EmptyState>{emptyMessage}</EmptyState>
            ) : (
              filteredOptions.map((opt, indice) => {
                const checked = (value || []).some((v) => String(v) === String(opt.value));
                // +1: la fila "Todas" ocupa el índice 0 de filasNavegables.
                const indiceNavegable = indice + 1;
                return (
                  <Fila
                    key={opt.value}
                    id={`opcion-${indiceNavegable}`}
                    ref={registrarOpcion(indiceNavegable)}
                    role="option"
                    aria-selected={checked}
                    $activo={indiceActivo === indiceNavegable}
                    onMouseEnter={() => setIndiceActivo(indiceNavegable)}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => toggleValor(opt.value)}
                  >
                    <CheckboxBox $checked={checked}>
                      {checked ? <i className="bi bi-check-lg" /> : null}
                    </CheckboxBox>
                    <span>{opt.label}</span>
                  </Fila>
                );
              })
            )}
          </Lista>
        </Panel>
      ) : null}
    </Wrapper>
  );
}
